/**
 * AI Coach Process Handlers
 *
 * All AI Coach-related query and mutation handlers
 */

import { handleRequest } from './config'
import { ApiResponse } from '../types'
import { State } from '../../hooks/useAppState'
import { loadCloudFunctionsHandler } from '../handlers'
import {
  AICoachSessionData,
  AICoachPart,
  GetAICoachSessionResponse,
  TriggerAICoachResponse
} from '../../../../pure-js/types/AICoach'

// ========================================
// QUERY HANDLERS
// ========================================

/**
 * Get the latest eligible AI Coach session for the current user.
 * @param state - The application state
 * @param sessionId - Optional session ID to fetch a specific session
 */
export const getAICoachSession = async (
  state: State,
  sessionId?: string
): Promise<ApiResponse<AICoachSessionData | undefined>> => {
  return handleRequest(
    async () => {
      // Mock data - return undefined (no session available)
      await new Promise((resolve) => setTimeout(resolve, 300))
      return {
        data: undefined,
        success: true
      } as ApiResponse<AICoachSessionData | undefined>
    },
    async () => {
      try {
        const cloudFunctions = await loadCloudFunctionsHandler()
        const response = await cloudFunctions.getAICoachSession({ sessionId }, state)

        if (!response.success) {
          console.warn('⚠️ [ProcessHandler] Cloud function call failed:', response.data?.error)
          return {
            data: undefined,
            success: false,
            error: response.data?.error || 'No AI Coach session available'
          }
        }

        const data = response.data as GetAICoachSessionResponse
        if (!data.session) {
          return {
            data: undefined,
            success: true
          }
        }

        return {
          data: data.session,
          success: true
        }
      } catch (error: unknown) {
        console.error('❌ [ProcessHandler] Error fetching AI Coach session:', error)
        const message = error instanceof Error ? error.message : 'Failed to fetch AI Coach session'
        return {
          data: undefined,
          success: false,
          error: message
        }
      }
    }
  )
}

// ========================================
// MUTATION HANDLERS
// ========================================

/**
 * Trigger AI Coach to speak a specific part
 * Starts AI speaking for the requested part
 */
export const triggerAICoach = async (
  sessionId: string,
  part: AICoachPart,
  state: State
): Promise<ApiResponse<TriggerAICoachResponse>> => {
  return handleRequest(
    async () => {
      // Mock data
      await new Promise((resolve) => setTimeout(resolve, 500))
      return {
        data: { success: true },
        success: true
      } as ApiResponse<TriggerAICoachResponse>
    },
    async () => {
      try {
        const cloudFunctions = await loadCloudFunctionsHandler()
        const response = await cloudFunctions.triggerAICoach({ sessionId, part }, state)

        if (!response.success || !response.data?.success) {
          const errorMessage = response.error || response.data?.error || 'Failed to trigger AI Coach'
          console.warn('⚠️ [ProcessHandler] Failed to trigger AI Coach:', errorMessage)
          return {
            data: { success: false, error: errorMessage },
            success: false,
            error: errorMessage
          }
        }

        return {
          data: response.data,
          success: true
        }
      } catch (error: unknown) {
        console.error('❌ [ProcessHandler] Error triggering AI Coach:', error)
        const message = error instanceof Error ? error.message : 'Failed to trigger AI Coach'
        return {
          data: { success: false, error: message },
          success: false,
          error: message
        }
      }
    }
  )
}

/**
 * Replay the AI Coach audio for the current part.
 * Re-triggers the assistant to speak the current step again for everyone.
 * Reuses cached audio and does not advance the session or reset readiness.
 */
export const replayAICoach = async (sessionId: string, state: State): Promise<ApiResponse<TriggerAICoachResponse>> => {
  return handleRequest(
    async () => {
      await new Promise((resolve) => setTimeout(resolve, 300))
      return {
        data: { success: true },
        success: true
      } as ApiResponse<TriggerAICoachResponse>
    },
    async () => {
      try {
        const cloudFunctions = await loadCloudFunctionsHandler()
        const response = await cloudFunctions.replayAICoach({ sessionId }, state)

        if (!response.success || !response.data?.success) {
          const errorMessage = response.error || response.data?.error || 'Failed to replay AI Coach'
          console.warn('⚠️ [ProcessHandler] Failed to replay AI Coach:', errorMessage)
          return {
            data: { success: false, error: errorMessage },
            success: false,
            error: errorMessage
          }
        }

        return {
          data: response.data,
          success: true
        }
      } catch (error: unknown) {
        console.error('❌ [ProcessHandler] Error replaying AI Coach:', error)
        const message = error instanceof Error ? error.message : 'Failed to replay AI Coach'
        return {
          data: { success: false, error: message },
          success: false,
          error: message
        }
      }
    }
  )
}
